import { OctopusMonoLogo } from '@octopus-do/octopus-storybook/icons'
import { isAiChatV2Enabled, selectAIAgentStatus } from '@store/reducers/ai'
import type { ChatAgentStatus } from '@store/reducers/ai/v2/types'
import type React from 'react'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { clsJoin } from '@/utils'
import css from '../../../../styles.module.css'

// Human-friendly label per activity status (plan 06 §3).
const STATUS_LABEL: Record<ChatAgentStatus, string> = {
  thinking: 'Thinking',
  checking: 'Checking',
  generating: 'Generating',
  editing: 'Editing',
}

interface AIMessageHeaderProps {
  isGenerating?: boolean
}

export const AIMessageHeader: React.FC<AIMessageHeaderProps> = ({
  isGenerating = false,
}) => {
  const v2 = isAiChatV2Enabled()
  // v2 (chat-agent): server-mapped activity status. Null on v1.
  const agentStatus = useSelector(selectAIAgentStatus)

  // v1 fallback: the old timed "Thinking → Still thinking → …" text.
  const [timedStatus, setTimedStatus] = useState<string | null>('Thinking')

  useEffect(() => {
    if (v2) {
      return undefined
    }

    if (!isGenerating) {
      setTimedStatus(null)
      return undefined
    }

    setTimedStatus('Thinking')
    const timers: ReturnType<typeof setTimeout>[] = []
    timers.push(setTimeout(() => setTimedStatus('Still thinking'), 10000))
    timers.push(setTimeout(() => setTimedStatus('A few more moments'), 15000))

    return () => {
      timers.forEach(clearTimeout)
    }
  }, [isGenerating, v2])

  // v2: show the activity status while generating (default 'thinking' until the
  // first status event arrives). v1: the timed text.
  const displayStatus = v2
    ? isGenerating
      ? STATUS_LABEL[agentStatus ?? 'thinking']
      : null
    : timedStatus

  if (!displayStatus) {
    return null
  }

  // Every v2 activity status shimmers in the purple↔pink gradient (per Maxim);
  // the timed v1 fallback stays grey.
  const colored = v2 && isGenerating

  return (
    <div
      className={clsJoin(
        css.ai_chat_sidebar_message_ai_head,
        isGenerating && css.ai_chat_sidebar_message_ai_head_generating,
        colored && css.ai_chat_sidebar_message_ai_head_colored
      )}
    >
      {isGenerating && <OctopusMonoLogo />}
      <span>{displayStatus}</span>
    </div>
  )
}
